import PauseIcon from "@mui/icons-material/Pause";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SkipPreviousIcon from "@mui/icons-material/SkipPrevious";
import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { orange, pink } from "@mui/material/colors";
import { useMemo, type FunctionComponent, type ReactNode } from "react";
import type DifferentiationMode from "../core/DifferentiationMode";
import type ExecutionStep from "../features/ExecutionStep";
import Katex from "../latex/Katex";

interface StepExecutionPanelProps {
  isPlaying: boolean;
  // Number of steps already revealed, from 0 to steps.length
  stepIndex: number;
  steps: ExecutionStep[];
  differentiationMode: DifferentiationMode;
  onStepForward: () => void;
  onStepBackward: () => void;
  onTogglePlay: () => void;
  onReset: () => void;
}

/**
 * The automatic walkthrough, one tab of the derivatives inspector.
 *
 * The topological order drives here: pressing Next reveals the next value the
 * graph would compute, whether or not that's the node the user is looking at.
 * The manual tab is the same walk with the order handed to the user.
 */
const StepExecutionPanel: FunctionComponent<StepExecutionPanelProps> = ({
  isPlaying,
  stepIndex,
  steps,
  differentiationMode,
  onStepForward,
  onStepBackward,
  onTogglePlay,
  onReset,
}) => {
  const totalSteps = steps.length;
  const isFinished = totalSteps > 0 && stepIndex >= totalSteps;

  // The step that has just been revealed, which is the one being explained
  const currentStep: ExecutionStep | null = useMemo(() => {
    if (stepIndex <= 0 || stepIndex > totalSteps) {
      return null;
    }
    return steps[stepIndex - 1];
  }, [stepIndex, steps, totalSteps]);

  // The node name is typeset rather than printed raw, so `a_1` reads as a with
  // a subscript 1 just like it does on the node itself
  const description = useMemo((): ReactNode => {
    if (totalSteps === 0) {
      return "Nothing to evaluate yet. Add operation nodes, then pick a derivative target.";
    }
    if (currentStep === null) {
      return "Press Step (or Play) to walk through the graph one node at a time.";
    }

    const prefix = isFinished ? "Done. " : "";
    const nodeName = <Katex latex={currentStep.nodeName} />;
    if (currentStep.phase === "value") {
      return (
        <>
          {`${prefix}Forward pass: evaluated f() of `}
          {nodeName}
          {" from its input values."}
        </>
      );
    }
    if (differentiationMode === "REVERSE") {
      return (
        <>
          {`${prefix}Reverse mode: accumulated the derivative of `}
          {nodeName}
          {" from its output nodes (chain rule)."}
        </>
      );
    }
    return (
      <>
        {`${prefix}Forward mode: accumulated the derivative of `}
        {nodeName}
        {" from its input nodes (chain rule)."}
      </>
    );
  }, [currentStep, differentiationMode, isFinished, totalSteps]);

  const phaseChip = useMemo(() => {
    if (currentStep === null) {
      return null;
    }
    const isValuePhase = currentStep.phase === "value";
    return (
      <Chip
        size="small"
        label={isValuePhase ? "f() value" : "derivative"}
        sx={{
          fontWeight: 600,
          color: "common.white",
          backgroundColor: isValuePhase ? orange[700] : pink[600],
        }}
      />
    );
  }, [currentStep]);

  return (
    <Box px={1.5} py={1} flexGrow={1} overflow="auto" sx={{ minHeight: 0 }}>
      <Stack spacing={0.75}>
        {/* Controls */}
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title="Previous step">
            {/* The span lets the tooltip work while the
                button is disabled */}
            <Box component="span" display="flex">
              <IconButton
                size="small"
                disabled={stepIndex <= 0}
                onClick={onStepBackward}
                aria-label="step-backward"
              >
                <SkipPreviousIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>

          <Tooltip title={isPlaying ? "Pause" : "Play all steps"}>
            {/* The span lets the tooltip work while the
                button is disabled */}
            <Box component="span" display="flex">
              <IconButton
                size="small"
                color="primary"
                disabled={totalSteps === 0 || isFinished}
                onClick={onTogglePlay}
                aria-label={isPlaying ? "pause-steps" : "play-steps"}
              >
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
            </Box>
          </Tooltip>

          <Tooltip title="Next step">
            {/* The span lets the tooltip work while the
                button is disabled */}
            <Box component="span" display="flex">
              <IconButton
                size="small"
                disabled={totalSteps === 0 || isFinished}
                onClick={onStepForward}
                aria-label="step-forward"
              >
                <SkipNextIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>

          <Tooltip title="Restart from the beginning">
            {/* The span lets the tooltip work while the
                button is disabled */}
            <Box component="span" display="flex">
              <IconButton
                size="small"
                disabled={stepIndex <= 0}
                onClick={onReset}
                aria-label="reset-steps"
              >
                <ReplayIcon fontSize="small" />
              </IconButton>
            </Box>
          </Tooltip>

          <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
            {totalSteps === 0 ? "0 / 0" : `${stepIndex} / ${totalSteps}`}
          </Typography>

          <Box flexGrow={1} display="flex" justifyContent="flex-end">
            {phaseChip}
          </Box>
        </Stack>

        {/* Progress */}
        <LinearProgress
          variant="determinate"
          value={totalSteps === 0 ? 0 : (stepIndex / totalSteps) * 100}
          sx={{ height: 4, borderRadius: 2 }}
        />

        {/* Current step description */}
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Stack>
    </Box>
  );
};

export default StepExecutionPanel;
